/**
 * Pure helpers for the pre-arrival split modal (Incoming Stock).
 *
 * A pre-arrival split moves selected quantities off a Drafts / Waiting Arrival
 * PO onto a -PARTx child so the two parts can carry different ETAs (the
 * supplier back-ordered some lines). Nothing has been received yet — this is
 * NOT the receive-time shortfall split in poReceiptSummary.js.
 *
 * The backend re-validates every quantity against the canonical cached PO;
 * these helpers only drive the modal UI and its optimistic updates.
 */

import { getOrderedPoLines } from './poReceiptSummary';

/** Coerce a move-qty input, clamped to [0, max]. Empty string stays empty for typing. */
export function sanitizeSplitQuantityInput(rawValue, max) {
  if (rawValue === '' || rawValue === null || rawValue === undefined) return '';
  const n = parseInt(rawValue, 10);
  if (!Number.isFinite(n) || n < 0) return '0';
  const cap = Number.isFinite(Number(max)) ? Number(max) : n;
  return String(Math.min(n, Math.max(0, cap)));
}

/** Splittable lines from a PO's (display) item list — ordered lines only, positive qty. */
export function buildPreArrivalSplitLines(items = []) {
  return getOrderedPoLines(items)
    .map((item) => ({
      sku: String(item?.sku || '').trim(),
      title: item?.title || '',
      variantTitle: item?.variantTitle || null,
      quantity: Math.floor(Number(item?.quantity)) || 0
    }))
    .filter((line) => line.sku && line.quantity > 0);
}

/**
 * Validate the operator's selections.
 * @param {Array} lines - from buildPreArrivalSplitLines
 * @param {Object} moveQtyBySku - { [sku]: string } raw input values
 * @returns {{ movedItems, totalMoved, totalAvailable, movesEverything, canSubmit }}
 *   movedItems carry { sku, quantity, title } for the API payload.
 */
export function summarizePreArrivalSplit(lines = [], moveQtyBySku = {}) {
  const movedItems = [];
  let totalMoved = 0;
  let totalAvailable = 0;

  for (const line of lines) {
    totalAvailable += line.quantity;
    const raw = moveQtyBySku[line.sku];
    const qty = parseInt(raw, 10);
    if (!Number.isFinite(qty) || qty <= 0) continue;
    const moved = Math.min(qty, line.quantity);
    movedItems.push({ sku: line.sku, quantity: moved, title: line.title });
    totalMoved += moved;
  }

  const movesEverything = totalAvailable > 0 && totalMoved >= totalAvailable;
  return {
    movedItems,
    totalMoved,
    totalAvailable,
    movesEverything,
    // Must move something, must leave something behind on this PO.
    canSubmit: movedItems.length > 0 && !movesEverything
  };
}

/**
 * Optimistic parent-card update after a successful split: subtract the moved
 * quantities from the displayed item list (extras untouched), dropping emptied
 * lines. Mirrors backend subtractSplitOffItemsFromLines — keep in sync.
 */
export function subtractMovedItemsFromPoItems(items = [], movedItems = []) {
  const movedBySku = new Map();
  for (const moved of movedItems) {
    const key = String(moved?.sku || '').trim().toUpperCase();
    const qty = Math.floor(Number(moved?.quantity));
    if (!key || !Number.isFinite(qty) || qty <= 0) continue;
    movedBySku.set(key, (movedBySku.get(key) || 0) + qty);
  }
  if (movedBySku.size === 0) return Array.isArray(items) ? items : [];

  const adjusted = [];
  for (const item of (Array.isArray(items) ? items : [])) {
    if (item?.isExtraItem === true) {
      adjusted.push(item);
      continue;
    }
    const key = String(item?.sku || '').trim().toUpperCase();
    const remaining = key ? movedBySku.get(key) : undefined;
    if (!remaining) {
      adjusted.push(item);
      continue;
    }
    const lineQty = Math.floor(Number(item?.quantity)) || 0;
    const take = Math.min(lineQty, remaining);
    movedBySku.set(key, remaining - take);
    const newQty = lineQty - take;
    if (newQty > 0) {
      adjusted.push({ ...item, quantity: newQty });
    }
  }
  return adjusted;
}
